import { Link, useLocation } from 'react-router-dom';
import { LogOut, X } from 'lucide-react';
import { useAuthStore } from '@/store/auth-store';
import { cn } from '@/lib/utils';
import { useFocusTrap } from '@/lib/accessibility';
import { hasActiveSubscription } from '@/lib/subscription';
import { useRef, useEffect } from 'react';
import { SidebarShareCard } from './SidebarShareCard';

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  showDot?: boolean;
  requiresActiveSubscription?: boolean;
}

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  navItems: NavItem[];
  profileLink: string | null;
}

export function MobileNav({ isOpen, onClose, navItems, profileLink }: MobileNavProps) {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const isSubscriptionActive = hasActiveSubscription(user?.subscription);
  const menuRef = useRef<HTMLDivElement>(null);

  // Focus trap for the mobile menu
  useFocusTrap(isOpen, menuRef);

  const handleLogout = () => {
    logout();
    onClose();
  };

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          'fixed inset-0 bg-night-900/70 z-40 transition-opacity md:hidden',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-out menu */}
      <div
        ref={menuRef}
        className={cn(
          'fixed top-0 right-0 h-full w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out md:hidden',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        id="mobile-navigation"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 pb-3 border-b border-lilac-900">
            <span className="font-display text-xl font-bold text-blueviolet">Menu</span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-lilac-500 transition-colors"
              aria-label="Close navigation menu"
            >
              <X className="h-5 w-5 text-blueviolet" aria-hidden="true" />
            </button>
          </div>

          {/* User info */}
          {user && (
            <div className="p-4 py-3 border-b border-lilac-900">
              <p className="font-body font-medium text-night-900">{user.firstName || user.email}</p>
              <p className="font-body text-sm text-night-500">{user.email}</p>
            </div>
          )}

          {/* Navigation items */}
          <nav
            className="flex flex-col flex-1 min-h-0 overflow-y-auto"
            role="navigation"
            aria-label="Main navigation"
          >
            <ul className="space-y-1 p-2">
              {navItems.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const hasDotBadge = Boolean(item.showDot);
                const isDisabled = Boolean(item.requiresActiveSubscription && !isSubscriptionActive);
                const ariaLabel = hasDotBadge
                  ? `${item.label} (new pending request)`
                  : item.label;

                const baseItemClasses = cn(
                  'flex items-center justify-between px-4 py-2 rounded-lg transition-colors',
                  isDisabled
                    ? 'cursor-not-allowed bg-lilac-100 text-night-300'
                    : isActive
                      ? 'bg-lilac-500 text-blueviolet'
                      : 'text-night-900 hover:bg-lilac-500 hover:text-blueviolet'
                );

                if (isDisabled) {
                  return (
                    <li key={item.path}>
                      <div className={baseItemClasses} aria-disabled="true" aria-label={ariaLabel}>
                        <div className="flex items-center space-x-3">
                          <Icon className="h-5 w-5" aria-hidden="true" />
                          <span className="font-body font-medium">{item.label}</span>
                        </div>
                        {hasDotBadge && (
                          <span
                            className="h-2.5 w-2.5 rounded-full bg-tigerlily"
                            aria-label="New pending request"
                          />
                        )}
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={onClose}
                      className={baseItemClasses}
                      aria-current={isActive ? 'page' : undefined}
                      aria-label={ariaLabel}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                        <span className="font-body font-medium">{item.label}</span>
                      </div>
                      {hasDotBadge && (
                        <span
                          className="h-2.5 w-2.5 rounded-full bg-tigerlily"
                          aria-label="New pending request"
                        />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <div className="mt-auto p-4 pt-0 space-y-3 overflow-hidden min-w-0">
              {/* Profile Link & Share */}
              {profileLink && <SidebarShareCard profileLink={profileLink} />}
            </div>
          </nav>

          {/* Logout button */}
          <div className="px-4 py-2 safe-area-pb-4 border-t border-lilac-900">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-night-900 hover:bg-lilac-500 hover:text-blueviolet transition-colors"
              aria-label="Log out of your account"
            >
              <LogOut className="h-5 w-5" aria-hidden="true" />
              <span className="font-body font-medium">Log Out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
